import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Grid, Paper, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { AgendaScheduleTypes, DEFAULT_CLINIC_TIMEZONE, IAgendasConfigParams } from '../../../constants/types';
import { getAgendasConfig } from '../../../services/agenda';
import { updateAgendaConfigAction } from '../../../redux/actions/investigationsActions';

const defaultParams = (): IAgendasConfigParams => ({
    timezone: DEFAULT_CLINIC_TIMEZONE,
    locale: 'en-US',
    consultation: {
        type: 'date_time',
        durationMins: 30,
        multipleAppointments: 1,
    },
    surgery: {
        type: 'date_time',
        durationMins: 60,
    },
});

// consultation and surgery expose the same set of controls; render one block per section.
interface SectionFieldsProps {
    idPrefix: 'consultation' | 'surgery';
    titleId: string;
    typeLabel: string;
    durationLabel: string;
    smsLabelPrefix: string;
    type: AgendaScheduleTypes;
    durationMins: number;
    multipleAppointments: boolean;
    smsSchedulesRaw: string;
    smsMessage: string;
    onType: (v: AgendaScheduleTypes) => void;
    onDuration: (v: number) => void;
    onMultiple: (v: boolean) => void;
    onSmsSchedulesRaw: (v: string) => void;
    onSmsMessage: (v: string) => void;
}

const SectionFields: React.FC<SectionFieldsProps> = ({
    idPrefix, titleId, typeLabel, durationLabel, smsLabelPrefix,
    type, durationMins, multipleAppointments, smsSchedulesRaw, smsMessage,
    onType, onDuration, onMultiple, onSmsSchedulesRaw, onSmsMessage,
}) => (
    <>
        <Grid item xs={12}><h5><Translate id={titleId} /></h5></Grid>
        <Grid item xs={12} sm={6}>
            <TextField
                select
                id={`${idPrefix}-schedule-type`}
                label={typeLabel}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                value={type}
                onChange={(e) => onType(e.target.value as AgendaScheduleTypes)}
            >
                <option value={AgendaScheduleTypes.DATE_TIME}>With hours</option>
                <option value={AgendaScheduleTypes.DATE}>By day</option>
            </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
            <TextField
                label={durationLabel}
                fullWidth
                size="small"
                type="number"
                value={durationMins}
                onChange={(e) => onDuration(Number(e.target.value))}
            />
        </Grid>
        <Grid item xs={12} sm={6}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={multipleAppointments}
                        onChange={(e) => onMultiple(e.target.checked)}
                    />
                }
                label="Allow Multiple Appointments"
            />
        </Grid>
        <Grid item xs={12} sm={6}>
            <TextField
                id={`${idPrefix}-sms-schedules`}
                label={`${smsLabelPrefix} SMS reminders (mins before, comma-separated)`}
                fullWidth
                size="small"
                value={smsSchedulesRaw}
                onChange={(e) => onSmsSchedulesRaw(e.target.value)}
            />
        </Grid>
        <Grid item xs={12} sm={6}>
            <TextField
                id={`${idPrefix}-sms-message`}
                label={`${smsLabelPrefix} SMS message (%X = appointment date)`}
                fullWidth
                size="small"
                value={smsMessage}
                onChange={(e) => onSmsMessage(e.target.value)}
            />
        </Grid>
    </>
);

const Agenda: React.FC = () => {
    const dispatch = useDispatch();
    const investigation = useSelector((state: any) => state.investigations.currentInvestigation);
    const [params, setParams] = useState<IAgendasConfigParams>(defaultParams());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [consultationSmsSchedulesRaw, setConsultationSmsSchedulesRaw] = useState('');
    const [surgerySmsSchedulesRaw, setSurgerySmsSchedulesRaw] = useState('');

    useEffect(() => {
        if (!investigation?.uuid) return;
        setLoading(true);
        setError(null);
        getAgendasConfig(investigation.uuid)
            .then((response) => {
                let next: IAgendasConfigParams | null = null;
                if (response.agendaConfig?.params) {
                    next = {
                        ...defaultParams(),
                        ...response.agendaConfig.params,
                        consultation: {
                            ...defaultParams().consultation,
                            ...(response.agendaConfig.params.consultation ?? {}),
                        },
                        surgery: {
                            ...defaultParams().surgery,
                            ...(response.agendaConfig.params.surgery ?? {}),
                        },
                    };
                } else if (investigation.outpatientsInfo) {
                    next = {
                        ...defaultParams(),
                        ...investigation.outpatientsInfo,
                    };
                }
                if (next) {
                    setParams(next);
                    // SMS schedule minutes are edited as a raw comma-separated string so
                    // the user can type separators freely; the parsed number[] lives in params.
                    setConsultationSmsSchedulesRaw(next.consultation?.sms?.schedules?.join(', ') ?? '');
                    setSurgerySmsSchedulesRaw(next.surgery?.sms?.schedules?.join(', ') ?? '');
                }
            })
            .catch(() => setError('load_failed'))
            .finally(() => setLoading(false));
    }, [investigation?.uuid]);

    // Comma-separated minutes-before → number[]; drops blanks/non-positive entries.
    const parseSchedules = (raw: string): number[] =>
        raw.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0);

    const setSectionSms = (
        section: 'consultation' | 'surgery',
        patch: Partial<{ schedules: number[]; message: string }>
    ) => {
        setParams((prev) => {
            if (section === 'consultation') {
                const sms = { schedules: [] as number[], message: '', ...(prev.consultation.sms ?? {}), ...patch };
                return { ...prev, consultation: { ...prev.consultation, sms } };
            }
            const base = prev.surgery ?? { type: AgendaScheduleTypes.DATE_TIME };
            const sms = { schedules: [] as number[], message: '', ...(base.sms ?? {}), ...patch };
            return { ...prev, surgery: { ...base, sms } };
        });
    };

    const handleSave = async () => {
        if (!investigation?.uuid) return;
        setSaving(true);
        setError(null);
        setSuccess(false);
        try {
            await dispatch(updateAgendaConfigAction(investigation.uuid, params) as any);
            setSuccess(true);
        } catch {
            setError('save_failed');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Translate id={`hospital.demo_configuration.agenda.${error}`} />
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    <Translate id="hospital.demo_configuration.save_success" />
                </Alert>
            )}
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Timezone"
                            fullWidth
                            size="small"
                            value={params.timezone ?? ''}
                            onChange={(e) => setParams({ ...params, timezone: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Locale"
                            fullWidth
                            size="small"
                            value={params.locale}
                            onChange={(e) => setParams({ ...params, locale: e.target.value })}
                        />
                    </Grid>
                    <SectionFields
                        idPrefix="consultation"
                        titleId="hospital.demo_configuration.agenda.consultation"
                        typeLabel="Consultation schedule type"
                        durationLabel="Default Duration (mins)"
                        smsLabelPrefix="Consultation"
                        type={params.consultation?.type ?? AgendaScheduleTypes.DATE_TIME}
                        durationMins={params.consultation?.durationMins ?? 30}
                        multipleAppointments={(params.consultation?.multipleAppointments ?? 0) > 0}
                        smsSchedulesRaw={consultationSmsSchedulesRaw}
                        smsMessage={params.consultation?.sms?.message ?? ''}
                        onType={(v) => setParams({ ...params, consultation: { ...params.consultation, type: v } })}
                        onDuration={(v) => setParams({ ...params, consultation: { ...params.consultation, durationMins: v } })}
                        onMultiple={(v) => setParams({ ...params, consultation: { ...params.consultation, multipleAppointments: v ? 1 : 0 } })}
                        onSmsSchedulesRaw={(v) => {
                            setConsultationSmsSchedulesRaw(v);
                            setSectionSms('consultation', { schedules: parseSchedules(v) });
                        }}
                        onSmsMessage={(v) => setSectionSms('consultation', { message: v })}
                    />
                    <SectionFields
                        idPrefix="surgery"
                        titleId="hospital.demo_configuration.agenda.surgery"
                        typeLabel="Surgery schedule type"
                        durationLabel="Surgery Duration (mins)"
                        smsLabelPrefix="Surgery"
                        type={params.surgery?.type ?? AgendaScheduleTypes.DATE_TIME}
                        durationMins={params.surgery?.durationMins ?? 60}
                        multipleAppointments={(params.surgery?.multipleAppointments ?? 0) > 0}
                        smsSchedulesRaw={surgerySmsSchedulesRaw}
                        smsMessage={params.surgery?.sms?.message ?? ''}
                        onType={(v) => setParams({ ...params, surgery: { ...(params.surgery ?? { type: AgendaScheduleTypes.DATE_TIME }), type: v } })}
                        onDuration={(v) => setParams({ ...params, surgery: { ...(params.surgery ?? { type: AgendaScheduleTypes.DATE_TIME }), durationMins: v } })}
                        onMultiple={(v) => setParams({ ...params, surgery: { ...(params.surgery ?? { type: AgendaScheduleTypes.DATE_TIME }), multipleAppointments: v ? 1 : 0 } })}
                        onSmsSchedulesRaw={(v) => {
                            setSurgerySmsSchedulesRaw(v);
                            setSectionSms('surgery', { schedules: parseSchedules(v) });
                        }}
                        onSmsMessage={(v) => setSectionSms('surgery', { message: v })}
                    />
                    <Grid item xs={12}>
                        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                            {saving ? <CircularProgress size={20} color="inherit" /> : <Translate id="general.save" />}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default Agenda;
